<template>
  <div>
    <b-row class="mb-1">
      <b-col class="d-flex justify-content-between align-items-center">
        <h1 class="font-weight-bolder mb-0">
          Tickets
        </h1>
        <b-button
          variant="primary"
          :to="{ name: 'ticketNew' }"
        >
          Nuevo ticket
        </b-button>
      </b-col>
    </b-row>

    <b-card class="mb-1">
      <b-row>
        <b-col md="4">
          <b-form-group label="Buscar">
            <b-form-input
              v-model.trim="filters.q"
              placeholder="Título o descripción"
              @keyup.enter="loadTickets"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Estado">
            <b-form-select
              v-model="filters.by_status"
              :options="statusOptions"
              @change="loadTickets"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Prioridad">
            <b-form-select
              v-model="filters.by_priority"
              :options="priorityOptions"
              @change="loadTickets"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="2"
          class="d-flex align-items-end"
        >
          <b-button
            block
            variant="outline-primary"
            @click="loadTickets"
          >
            Filtrar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay :show="loading">
      <b-card>
        <b-table
          :items="tickets"
          :fields="fields"
          responsive
          small
          show-empty
          empty-text="No hay tickets"
        >
          <template #cell(ticket_number)="row">
            #{{ row.item.ticket_number }}
          </template>

          <template #cell(priority)="row">
            <b-form-select
              :value="row.item.priority"
              :options="inlinePriorityOptions"
              size="sm"
              class="ticket-inline-select"
              :disabled="isRowUpdating(row.item.id)"
              @change="onInlinePriorityChange(row.item, $event)"
            />
          </template>

          <template #cell(status)="row">
            <b-form-select
              :value="row.item.status"
              :options="inlineStatusOptions"
              size="sm"
              class="ticket-inline-select"
              :disabled="isRowUpdating(row.item.id)"
              @change="onInlineStatusChange(row.item, $event)"
            />
          </template>

          <template #cell(requester_name)="row">
            {{ requesterLabel(row.item) }}
          </template>

          <template #cell(created_at)="row">
            {{ formatDate(row.item.created_at) }}
          </template>

          <template #cell(estimated_due_at)="row">
            {{ formatDate(row.item.estimated_due_at) }}
          </template>

          <template #cell(estimate_time_status)="row">
            <b-badge :variant="timeStatusVariant(row.item.estimate_time_status)">
              {{ timeStatusLabel(row.item.estimate_time_status) }}
            </b-badge>
          </template>

          <template #cell(criticality)="row">
            <b-badge :variant="criticalityVariant(row.item.criticality)">
              {{ row.item.criticality || 'n/a' }}
            </b-badge>
          </template>

          <template #cell(actions)="row">
            <b-button
              size="sm"
              variant="outline-primary"
              :to="{ name: 'ticketDetail', params: { ticketId: row.item.id } }"
            >
              Ver
            </b-button>
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center mt-1">
          <small>
            Página {{ pagination.page || 1 }} de {{ pagination.total_pages || 1 }}
          </small>
          <div>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-1"
              :disabled="(pagination.page || 1) <= 1 || loading"
              @click="changePage((pagination.page || 1) - 1)"
            >
              Anterior
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="(pagination.page || 1) >= (pagination.total_pages || 1) || loading"
              @click="changePage((pagination.page || 1) + 1)"
            >
              Siguiente
            </b-button>
          </div>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    return {
      fields: [
        { key: 'ticket_number', label: 'ID' },
        { key: 'title', label: 'Título' },
        { key: 'ticket_type', label: 'Tipo' },
        { key: 'requester_name', label: 'Solicitante' },
        { key: 'priority', label: 'Prioridad' },
        { key: 'status', label: 'Estado' },
        { key: 'created_at', label: 'Creado' },
        { key: 'estimated_due_at', label: 'Vence' },
        { key: 'estimate_time_status', label: 'Tiempo' },
        { key: 'criticality', label: 'Criticidad' },
        { key: 'estimated_days', label: 'Días' },
        { key: 'actions', label: 'Acciones' },
      ],
      filters: {
        q: '',
        by_status: '',
        by_priority: '',
      },
      page: 1,
      perPage: 10,
      rowUpdating: {},
      statusValues: [
        'backlog',
        'pending_specification',
        'ready_for_development',
        'in_development',
        'in_review',
        'done',
        'rejected',
      ],
      priorityOptions: [
        { value: '', text: 'Todas' },
        { value: 'low', text: 'Baja' },
        { value: 'medium', text: 'Media' },
        { value: 'high', text: 'Alta' },
        { value: 'urgent', text: 'Urgente' },
      ],
    }
  },
  computed: {
    ...mapGetters('tickets', ['tickets', 'ticketsPagination', 'loading']),
    pagination() {
      return this.ticketsPagination || {}
    },
    statusOptions() {
      return [
        { value: '', text: 'Todos' },
        ...this.statusValues.map(value => ({ value, text: this.statusLabel(value) })),
      ]
    },
    inlineStatusOptions() {
      return this.statusValues.map(value => ({ value, text: this.statusLabel(value) }))
    },
    inlinePriorityOptions() {
      return this.priorityOptions.filter(option => option.value)
    },
  },
  created() {
    this.loadTickets()
  },
  methods: {
    ...mapActions('tickets', ['fetchTickets', 'updateTicketInline']),
    statusLabel(value) {
      if (!value) return '-'
      const key = `ticketStatus.${value}`
      const translated = this.$t(key)
      return translated === key ? value : translated
    },
    timeStatusLabel(value) {
      if (!value) return '-'
      const key = `ticketTimeStatus.${value}`
      const translated = this.$t(key)
      return translated === key ? value : translated
    },
    requesterLabel(item) {
      const name = item?.requester_name
      const email = item?.requester_email
      if (name && email) return `${name} (${email})`
      return name || email || '-'
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleDateString()
    },
    async loadTickets() {
      await this.fetchTickets({
        ...this.filters,
        meta: {
          pagination: {
            page: this.page,
            per_page: this.perPage,
          },
        },
        by_column: {
          column: 'created_at',
          order: 'DESC',
        },
      })
    },
    async changePage(nextPage) {
      this.page = nextPage
      await this.loadTickets()
    },
    async onInlinePriorityChange(ticket, value) {
      await this.updateInlineField(ticket, 'priority', value)
    },
    async onInlineStatusChange(ticket, value) {
      await this.updateInlineField(ticket, 'status', value)
    },
    async updateInlineField(ticket, field, value) {
      if (!ticket?.id || !field || !value) return
      if (ticket[field] === value) return

      const ticketKey = String(ticket.id)
      if (this.rowUpdating[ticketKey]) return

      const previousValue = ticket[field]
      this.$set(ticket, field, value)
      this.$set(this.rowUpdating, ticketKey, true)

      try {
        const response = await this.updateTicketInline({
          id: ticket.id,
          ticket: { [field]: value },
        })

        const refreshed = response?.data || response
        if (refreshed && typeof refreshed === 'object') {
          Object.assign(ticket, refreshed)
        }
      } catch (error) {
        this.$set(ticket, field, previousValue)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.messages?.[0] || 'No se pudo actualizar el ticket',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.$delete(this.rowUpdating, ticketKey)
      }
    },
    isRowUpdating(ticketId) {
      return Boolean(this.rowUpdating[String(ticketId)])
    },
    priorityVariant(value) {
      if (value === 'urgent' || value === 'high') return 'danger'
      if (value === 'medium') return 'warning'
      return 'secondary'
    },
    criticalityVariant(value) {
      if (value === 'blocker' || value === 'high') return 'danger'
      if (value === 'medium') return 'warning'
      return 'secondary'
    },
    timeStatusVariant(value) {
      if (value === 'overdue' || value === 'completed_overdue') return 'danger'
      if (value === 'completed_on_time') return 'success'
      if (value === 'on_time') return 'primary'
      return 'secondary'
    },
  },
}
</script>

<style scoped>
.ticket-inline-select {
  min-width: 155px;
}
</style>
